import React, { useCallback, useEffect, useState } from 'react';
import { Icon } from 'semantic-ui-react';
import { getImageUrl } from "../../../utils/images";
import HeroButton from './HeroButton'

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;


const HeroView = ({ data = {} }) => {
  const {
    backgroundImage,
    autoplayMs,
    slides = [],
  } = data;

  const backgroundImageUrl = getImageUrl(backgroundImage);
  const [active, setActive] = useState(0);
  const [animDir, setAnimDir] = useState('next');
  const reduceMotion = prefersReducedMotion();

  useEffect(() => {
    if (active >= slides.length) setActive(0);
  }, [active, slides.length]);

  const goTo = useCallback((idx, dir) => {
    const n = slides.length;
    if (!n) return;
    setAnimDir(dir);
    setActive(((idx % n) + n) % n);
  }, [slides.length]);

  const next = useCallback(() => goTo(active + 1, 'next'), [active, goTo]);
  const prev = useCallback(() => goTo(active - 1, 'prev'), [active, goTo]);

  useEffect(() => {
    if (!autoplayMs || autoplayMs < 1000) return;
    if (reduceMotion) return;
    if (slides.length <= 1) return;

    const t = setInterval(() => {
      setAnimDir('next');
      setActive((v) => (v + 1) % slides.length);
    }, autoplayMs);

    return () => clearInterval(t);
  }, [autoplayMs, slides.length, reduceMotion]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, prev]);

  if (!slides.length) return null;

  return (
    <section
        className="c-hero"
        data-animdir={animDir}
        style={
          backgroundImageUrl
            ? { '--c-hero-bg-image': `url(${backgroundImageUrl})` }
            : undefined
        }
      >
      <div className="c-hero__inner">
        <Icon
          name="left chevron"
          size="big"
          circular
          onClick={prev}
          aria-label="Previous slide"
          className="c-hero__arrow c-hero__arrow--left"
        />
        <div className="c-hero__viewport" aria-live="polite">
          <div
            className="c-hero__track"
            style={{ '--c-hero-active': active }}
          >

          {slides.map((slide, i) => {
            const isActive = i === active;
            const layout = slide.layout ?? 'center';
            const imageUrl = getImageUrl(slide.image);
            const buttons = slide.buttons ?? [];

            return (
              <div
                key={slide.id || `slide-${i}`}
                className={`c-hero__slide ${isActive ? 'is-active' : ''} c-hero__slide--${layout}`}
                aria-hidden={!isActive}
              >
                {layout === 'split' ? (
                  <div className="c-hero__split">
                    <div className="c-hero__splitLeft">
                      <h1 className="c-hero__title">{slide.title}</h1>
                      <p className="c-hero__subtitle">{slide.subtitle}</p>

                      <div className="c-hero__actions c-hero__actions--split">
                        {buttons.map((btn, idx) => (
                          <HeroButton key={idx} button={btn} />
                        ))}
                      </div>
                    </div>

                    <div className="c-hero__splitRight">
                      <img
                        className="c-hero__slideImg c-hero__slideImg--right"
                        src={imageUrl}
                        alt=""
                      />
                    </div>
                  </div>
                ) : (
                  <div className="c-hero__content">
                    <h1 className="c-hero__title">{slide.title}</h1>
                    <p className="c-hero__subtitle">{slide.subtitle}</p>

                    <div className="c-hero__actions">
                      {buttons.map((btn, idx) => (
                        <HeroButton key={idx} button={btn} />
                      ))}
                    </div>

                    <div className="c-hero__media">
                      <img
                        className="c-hero__slideImg"
                        src={imageUrl}
                        alt=""
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          </div>
        </div>

        <div className="c-hero__dots" role="tablist" aria-label="Slides">
          {slides.map((slide, i) => (
            <button
              key={slide.id || `dot-${i}`}
              type="button"
              className={`c-hero__dot ${i === active ? 'is-active' : ''}`}
              onClick={() => goTo(i, i > active ? 'next' : 'prev')}
              aria-label={`Go to slide ${i + 1}`}
              aria-pressed={i === active}
            />
          ))}
        </div>

        <Icon
          name="right chevron"
          size="big"
          circular
          onClick={next}
          aria-label="Next slide"
          className="c-hero__arrow c-hero__arrow--right"
        />
      </div>
    </section>
  );
};

export default HeroView;